import React, { useEffect, useState } from "react";
import api from "../api"; // 1. Use your custom api instance
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Navigation } from "swiper/modules";
import { API } from '../config';
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./ProjectCard.css";
import EnquiryModal from "./EnquiryModal";

// Helper to handle both Cloudinary and Local paths
const getFullUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path; 
  return `${API}${path}`; 
};

function MiniProjectLoader() {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="container my-5 d-flex justify-content-center align-items-center">
      <div
        className="position-relative text-center px-4 py-4"
        style={{
          width: "100%",
          maxWidth: "360px",
          borderRadius: "28px",
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border: "1px solid rgba(255,255,255,0.3)",
          
        }}
      >
        {/* animated circles */}
        <div className="d-flex justify-content-center gap-2 mb-3">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ff7a00, #ffb347)",
                animation: "loaderBounce 1.4s infinite ease-in-out",
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>

        <h5 className="fw-semibold mb-1">
          Loading Properties & Projects
        </h5>

        <p
          className="text-muted mb-3"
          style={{ fontSize: "14px" }}
        >
          First visit might take few seconds...
        </p>

        {/* minimal progress */}
        <div
          style={{
            height: "5px",
            borderRadius: "999px",
            background: "#ececec",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(seconds * 5, 90)}%`,
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, #db6f0a, #dd7119)",
              transition: "width 1s ease",
            }}
          />
        </div>

        <small className="text-muted d-block mt-2">
          {seconds}s elapsed
        </small>
      </div>
    </section>
  );
}

export default function ProjectCard() {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedBrochure, setSelectedBrochure] = useState("");
  const [prefillMessage, setPrefillMessage] = useState("");

  useEffect(() => {
    api.get("/projects")
      .then((res) => setProjects(res.data))
      .catch((err) => console.error("Error fetching projects:", err));
  }, []);

  // testing loader
  // useEffect(() => {
  // const fetchProjects = async () => {
  //   try {
  //     await new Promise((resolve) =>
  //       setTimeout(resolve, 15000)
  //     );

  //     const res = await api.get("/projects");
  //     setProjects(res.data);
  //   } catch (err) {
  //     console.error("Error fetching projects:", err);
  //   }
  // };
  // fetchProjects();
  // }, []);

  if (projects.length === 0) {
  return <MiniProjectLoader />;
}

  const handleBrochureClick = (brochureUrl, projectTitle) => {
    setSelectedBrochure(getFullUrl(brochureUrl)); // Store full URL
    setPrefillMessage(`I am interested in the brochure for: ${projectTitle}`);
    setShowModal(true);
  };

  const handleFormSuccess = () => {
    if (selectedBrochure) {
      window.open(selectedBrochure, "_blank"); // Opens full URL (Cloudinary or Local)
      setSelectedBrochure("");
    }
  };

  return (
    <section className="project-card-section container my-5">
      <Swiper modules={[Navigation]} spaceBetween={30} slidesPerView={1} navigation={false}>
        {projects.map((project) => (
          <SwiperSlide key={project._id}>
            <ProjectSlide project={project} onBrochureClick={handleBrochureClick} />
          </SwiperSlide>
        ))}
        <CustomNavButtons />
      </Swiper>

      {showModal && (
        <EnquiryModal
          onClose={() => setShowModal(false)}
          onSuccess={handleFormSuccess}
          prefillMessage={prefillMessage}
        />
      )}
    </section>
  );
}

function ProjectSlide({ project, onBrochureClick }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Process all images through getFullUrl
  const projectImages = project.images.map(img => getFullUrl(img));

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? projectImages.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === projectImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="project-card d-flex flex-column flex-lg-row">
      <div className="image-container mx-auto" style={{ maxWidth: "350px" }}>
        <img
          src={projectImages[currentIndex]} // Correct URL
          alt={project.title}
          className="img-fluid"
        />
        <button className="nav-btn left" onClick={prevImage}>❮</button>
        <button className="nav-btn right" onClick={nextImage}>❯</button>

        <div className="dots">
          {projectImages.map((_, index) => (
            <span
              key={index}
              className={index === currentIndex ? "dot active" : "dot"}
              onClick={() => setCurrentIndex(index)}
            ></span>
          ))}
        </div>
      </div>

      <div className="details-container py-4 ps-lg-5 ms-lg-5">
        <h2>{project.title}</h2>
        <h3>{project.subtitle}</h3>
        <p>{project.description}</p>

        <ul>
          {project.features.map((feature, idx) => (
            <li key={idx}>{feature}</li>
          ))}
        </ul>

        <p className="fw-bold">Where business meets opportunity!</p>
        <p>Book your space now – Limited availability!</p>

        <div className="d-flex gap-2 flex-wrap">
          <button
            className="standard-btn w-auto"
            onClick={() => onBrochureClick(project.brochure, project.title)}
          >
            Download Brochure
          </button>
          <a
            href={`https://wa.me/${project.whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="standard-btn w-auto text-decoration-none"
          >
            WhatsApp
          </a>
          <a
            href={`mailto:${project.email}`}
            className="standard-btn w-auto text-decoration-none"
          >
            Email Us
          </a>
        </div>
      </div>
    </div>
  );
}

function CustomNavButtons() {
  const swiper = useSwiper();
  return (
    <div className="custom-nav-buttons text-center mt-3">
      <button className="btn btn-outline-dark mx-2" onClick={() => swiper.slidePrev()}>
        ⬅ Prev Project
      </button>
      <button className="btn btn-outline-dark mx-2" onClick={() => swiper.slideNext()}>
        Next Project ➡
      </button>
    </div>
  );
}
